import {
  Button,
  Checkbox,
  Col,
  ColProps,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
} from "antd";
import { ProColumns } from "@ant-design/pro-table";
import AntdTablePro from "components/antdTablePro";
import {
  useInvoiceConnect,
  useInvoiceConnectionList,
  useInvoiceDisconnect,
} from "hooks/queries/useInvoice";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiTrash } from "react-icons/bi";
import { InstallationSchema } from "types/installation";
import { ORDER_NO_LENGTH } from "utils/global/constants";

type InvoiceConnectionForm = {
  bookingNumber: string;
};

const colStyles: ColProps = {
  xs: 24,
  md: 12,
};

export default function InvoiceConnection({
  installation,
}: {
  installation: InstallationSchema;
}) {
  const { t } = useTranslation();
  const [connectBooking, setConnectBooking] = useState(false);

  const [form] = Form.useForm<InvoiceConnectionForm>();

  const { data } = useInvoiceConnectionList(
    installation.id,
    installation.invoice!.id // This components only renders if there is an invoice
  );

  const { mutate, isLoading: isConnectLoading } = useInvoiceConnect(
    installation.id,
    installation.invoice!.id // This components only renders if there is an invoice
  );

  const { mutate: disconnectMutate, isLoading: isDisconnectLoading } =
    useInvoiceDisconnect(installation.id, installation.invoice!.id);

  const onSubmit = async (data: InvoiceConnectionForm) => {
    if (!data.bookingNumber) return;
    mutate(data, {
      onSuccess: () => {
        message.success(t("general:updatedSuccess"));
        form.setFieldsValue({
          bookingNumber: "",
        });
        setConnectBooking(false);
      },
      onError: (e) => {
        if ((e as any)?.data?.message === "Installation already has invoice") {
          message.error(t("invoice:connect.alreadyConnected"));
          return;
        } else {
          message.error(t("general:updateFail"));
        }
      },
    });
  };

  const onDisconnect = (bookingNumber: string) => {
    disconnectMutate(bookingNumber, {
      onSuccess: () => {
        message.success(t("general:deleteSuccess"));
      },
      onError: () => {
        message.error(t("general:deleteFail"));
      },
    });
  };

  const connections =
    data?.data.filter((con) => con !== installation.payment.paymentId) ?? [];

  const columns: ProColumns[] = [
    {
      key: "connection",
      title: t("invoice:connect.connectedTo"),
      render: (_, connection: string) => {
        return <div>{connection}</div>;
      },
    },
    {
      key: "actions",
      title: t("general:action"),
      width: 100,
      render: (_, connection: string) => {
        return (
          <Popconfirm
            title={t("general:confirmDelete")}
            onConfirm={() => onDisconnect(connection)}
            okText={t("general:yes")}
            cancelText={t("general:no")}
          >
            <Button
              type="text"
              danger
              icon={<BiTrash />}
              loading={isDisconnectLoading}
            />
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <Checkbox
        checked={connectBooking}
        onChange={(e) => setConnectBooking(e.target.checked)}
        className="font-600"
      >
        {t("invoice:connect.title")}
      </Checkbox>
      <div>
        {connectBooking && (
          <Form
            form={form}
            validateTrigger="onChange"
            onFinish={onSubmit}
            autoComplete="off"
          >
            <Row gutter={16}>
              <Col {...colStyles}>
                <Form.Item
                  name="bookingNumber"
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (!value) return Promise.resolve();
                        const nan = Number.isNaN(value);
                        if (nan)
                          return Promise.reject(t("warnings:invalidValue"));
                        if (value.length !== ORDER_NO_LENGTH)
                          return Promise.reject(t("warnings:invalidValue"));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder={`${t("general:example")}: 2400000`} />
                </Form.Item>
              </Col>
              <Col {...colStyles}>
                <Button
                  key="save"
                  type="primary"
                  htmlType="submit"
                  loading={isConnectLoading}
                >
                  {t("invoice:connect.connect")}
                </Button>
              </Col>
            </Row>
          </Form>
        )}
        {connections.length > 0 && (
          <React.Fragment>
            <AntdTablePro
              columns={columns}
              rowKey={(record) => record}
              dataSource={connections}
              pagination={false}
              toolBarRender={false}
              className="mb-4"
            />
          </React.Fragment>
        )}
      </div>
    </div>
  );
}
